// Tells the room whether I'm here, for push (#683). A computer's browser reports `{t: "active", active, lastInputAt}`: in
// front (visible and focused) and when its last input was, so the room can hold my phone's push until I have been idle
// for my setting's minutes (shared/src/idlePush.ts). A phone's browser sends no `lastInputAt` and counts as active only
// while it is in front and was touched in the last few minutes, as before.
import { idleInput, idleReportSent, idleSetFront, newIdleReporter, nextIdleReport, type IdleReporter } from "@bloom/shared";

/** A phone's browser: no input for this long while in front, and it counts as away. */
export const MOBILE_IDLE_MS = 3 * 60_000;

export interface ActiveFrame {
  t: "active";
  active: boolean;
  lastInputAt?: number;
}

export class ActiveReporter {
  private r: IdleReporter = newIdleReporter();
  private lastInput: number;
  private legacySent = false;
  private readonly now: () => number;

  constructor(
    private o: {
      /** A phone's browser sends no lastInputAt. */
      mobile: boolean;
      /** Visible and focused. */
      front: () => boolean;
      /** Sends the frame; false: the socket is not open, so try again later. */
      send: (f: ActiveFrame) => boolean;
      now?: () => number;
    },
  ) {
    this.now = o.now ?? Date.now;
    this.lastInput = this.now();
  }

  /** Key, pointer, wheel or touch. */
  input() {
    const t = this.now();
    this.lastInput = t;
    idleInput(this.r, t);
    this.check();
  }

  /** Focus, visibility or the clock changed: send a frame if there is news. */
  check() {
    const now = this.now();
    if (this.o.mobile) {
      const active = this.o.front() && now - this.lastInput < MOBILE_IDLE_MS;
      if (active !== this.legacySent && this.o.send({ t: "active", active })) this.legacySent = active;
      return;
    }
    idleSetFront(this.r, this.o.front(), now);
    const f = nextIdleReport(this.r, now);
    if (!f) return;
    if (this.o.send(f.active ? { t: "active", active: true, lastInputAt: f.lastInputAt } : { t: "active", active: false })) idleReportSent(this.r, f, now);
  }

  /** A new socket starts out inactive in the room: say it all again. */
  reset() {
    this.legacySent = false;
    this.r.sentFront = null;
    this.r.sentAt = null;
    this.r.sentInputAt = null;
  }
}
